import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, DownloadSimple, Copy, Sparkle, FloppyDisk } from "@phosphor-icons/react";

const ASPECTS = [
  ["16:9", "Landscape 16:9", "LinkedIn · YouTube"],
  ["1:1", "Square 1:1", "Instagram"],
  ["9:16", "Vertical 9:16", "Reels · TikTok"],
];

const captionToText = (v) =>
  v ? [v.caption, (v.hashtags || []).map((h) => `#${h}`).join(" ")].filter(Boolean).join("\n\n") : "";

export default function SocialClipStudio({ slug }) {
  const base = `/admin/learning/social/${slug}`;
  const latestSlug = useRef(slug);
  const [socialDl, setSocialDl] = useState(null);
  const [headline, setHeadline] = useState("");
  const [headlineSaved, setHeadlineSaved] = useState("");
  const [headlineLoading, setHeadlineLoading] = useState(false);
  const [headlineSaving, setHeadlineSaving] = useState(false);
  const [captions, setCaptions] = useState(null);
  const [captionTab, setCaptionTab] = useState("linkedin");
  const [captionLoading, setCaptionLoading] = useState(false);
  const [captionText, setCaptionText] = useState({ linkedin: "", social: "" });
  const [tone, setTone] = useState("balanced");

  const downloadSocial = async (aspect) => {
    setSocialDl(aspect);
    try {
      const res = await api.get(`${base}/clip`, { params: { aspect }, responseType: "blob" });
      const label = aspect === "1:1" ? "square" : aspect === "9:16" ? "vertical" : "landscape";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${slug}-${label}-clip.mp4`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not build clip"); }
    finally { setSocialDl(null); }
  };

  const loadCaptions = useCallback(async (regenerate = false, toneArg = "balanced") => {
    setCaptionLoading(true);
    try {
      const cfg = regenerate ? { params: { regenerate: true, tone: toneArg } } : {};
      const { data } = await api.get(`${base}/caption`, cfg);
      if (latestSlug.current !== slug) return;  // guide switched mid-flight — drop stale response
      setCaptions(data);
      setCaptionText({ linkedin: captionToText(data.linkedin), social: captionToText(data.social) });
    } catch (e) { if (latestSlug.current === slug) toast.error(e.response?.data?.detail || "Could not load captions"); }
    finally { if (latestSlug.current === slug) setCaptionLoading(false); }
  }, [base, slug]);

  const regenerateWithTone = (t) => { setTone(t); loadCaptions(true, t); };

  const loadHeadline = useCallback(async () => {
    setHeadlineLoading(true);
    try {
      const { data } = await api.get(`${base}/headline`);
      if (latestSlug.current !== slug) return;  // guide switched mid-flight — drop stale response
      setHeadline(data.headline || ""); setHeadlineSaved(data.headline || "");
    } catch { /* headline is optional */ }
    finally { if (latestSlug.current === slug) setHeadlineLoading(false); }
  }, [base, slug]);

  useEffect(() => {
    latestSlug.current = slug;
    setCaptions(null); setCaptionTab("linkedin"); setCaptionText({ linkedin: "", social: "" }); setTone("balanced");
    setHeadline(""); setHeadlineSaved("");
    loadCaptions(); loadHeadline();
  }, [slug, loadCaptions, loadHeadline]);

  const saveHeadline = async () => {
    const h = headline.trim();
    if (!h) return;
    setHeadlineSaving(true);
    try {
      const { data } = await api.put(`${base}/headline`, { headline: h });
      setHeadline(data.headline); setHeadlineSaved(data.headline);
      toast.success("Headline saved — square & vertical clips rebuild on next download");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save headline"); }
    finally { setHeadlineSaving(false); }
  };

  const copyCaption = async (tab) => {
    try { await navigator.clipboard.writeText(captionText[tab] || ""); toast.success("Caption copied to clipboard"); }
    catch { toast.error("Could not copy"); }
  };

  const copyAndDownload = async (tab) => {
    await copyCaption(tab);
    await downloadSocial(tab === "linkedin" ? "16:9" : "9:16");
  };

  return (
    <div className="rounded-xl bg-navy text-white p-4" data-testid="social-studio">
      <div data-testid="clip-headline">
        <div className="text-[0.7rem] uppercase tracking-wide font-semibold text-white/40 mb-1.5">
          Clip headline <span className="text-white/30 normal-case tracking-normal">· shown on square &amp; vertical clips</span>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="clip-headline-input" value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={60}
            placeholder={headlineLoading ? "Loading…" : "Punchy headline for muted feeds"}
            className="flex-1 rounded-lg bg-white/95 text-navy text-xs font-semibold px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-orangeink/50" />
          <button onClick={saveHeadline} disabled={headlineSaving || !headline.trim() || headline.trim() === headlineSaved} data-testid="clip-headline-save"
            className="inline-flex items-center gap-1 text-xs font-semibold text-navy bg-white hover:bg-white/90 rounded-lg px-3 py-2 transition-colors disabled:opacity-50">
            {headlineSaving ? <CircleNotch size={13} className="animate-spin" /> : <FloppyDisk size={13} weight="bold" />} Save
          </button>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-white/10" data-testid="social-clips">
        <div className="text-[0.7rem] uppercase tracking-wide font-semibold text-white/40 mb-2">Download social clip</div>
        <div className="flex flex-wrap gap-2">
          {ASPECTS.map(([asp, label, hint]) => (
            <button key={asp} onClick={() => downloadSocial(asp)} disabled={!!socialDl} data-testid={`social-clip-${asp.replace(":", "x")}`}
              className="inline-flex items-center gap-2 text-xs font-semibold text-navy bg-white hover:bg-white/90 rounded-lg px-3 py-2 transition-colors disabled:opacity-60">
              {socialDl === asp ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={14} weight="bold" />}
              <span className="text-left leading-tight">{label}<span className="block text-[0.62rem] font-normal text-navy/50">{hint}</span></span>
            </button>
          ))}
        </div>
        <p className="text-white/40 text-[0.7rem] mt-2">Square &amp; vertical bake the headline as a caption bar on navy. First build of each ratio takes a few seconds.</p>
      </div>

      <div className="mt-3 pt-3 border-t border-white/10" data-testid="social-captions">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[0.7rem] uppercase tracking-wide font-semibold text-white/40">Post-ready caption</div>
          <button onClick={() => loadCaptions(true, tone)} disabled={captionLoading} data-testid="caption-regenerate"
            className="inline-flex items-center gap-1 text-[0.7rem] font-semibold text-white/70 hover:text-white disabled:opacity-50 transition-colors">
            {captionLoading ? <CircleNotch size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />} Regenerate
          </button>
        </div>
        <div className="flex items-center gap-1.5 mb-2" data-testid="caption-tone">
          <span className="text-[0.62rem] uppercase tracking-wide text-white/40 font-semibold mr-0.5">Tone</span>
          {[["formal", "Formal"], ["balanced", "Balanced"], ["playful", "Playful"]].map(([k, label]) => (
            <button key={k} onClick={() => regenerateWithTone(k)} disabled={captionLoading} data-testid={`caption-tone-${k}`}
              className={`text-[0.65rem] font-semibold px-2 py-0.5 rounded-full border transition-colors disabled:opacity-50 ${tone === k ? "bg-white text-navy border-white" : "border-white/20 text-white/60 hover:text-white hover:border-white/40"}`}>{label}</button>
          ))}
        </div>
        <div className="inline-flex rounded-lg bg-white/10 p-0.5 mb-2">
          {[["linkedin", "LinkedIn / YouTube"], ["social", "Instagram / TikTok"]].map(([k, label]) => (
            <button key={k} onClick={() => setCaptionTab(k)} data-testid={`caption-tab-${k}`}
              className={`text-[0.7rem] font-semibold px-2.5 py-1 rounded-md transition-colors ${captionTab === k ? "bg-white text-navy" : "text-white/70 hover:text-white"}`}>{label}</button>
          ))}
        </div>
        {captionLoading && !captions ? (
          <div className="text-white/50 text-xs flex items-center gap-2 py-3"><CircleNotch size={14} className="animate-spin" /> Writing an on-brand caption…</div>
        ) : (
          <>
            <textarea data-testid="caption-textarea" rows={6} value={captionText[captionTab]}
              onChange={(e) => setCaptionText((s) => ({ ...s, [captionTab]: e.target.value }))}
              className="w-full rounded-lg bg-white/95 text-navy text-xs leading-relaxed p-2.5 focus:outline-none focus:ring-2 focus:ring-orangeink/50" />
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <button onClick={() => copyAndDownload(captionTab)} disabled={!captionText[captionTab] || !!socialDl} data-testid="caption-copy-download"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-2 transition-transform hover:scale-[1.02] disabled:opacity-60" style={{ background: "#F37A24" }}>
                {socialDl ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={14} weight="bold" />} Copy caption &amp; download clip
              </button>
              <button onClick={() => copyCaption(captionTab)} disabled={!captionText[captionTab]} data-testid="caption-copy"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy bg-white hover:bg-white/90 rounded-lg px-3 py-2 transition-colors disabled:opacity-60">
                <Copy size={14} weight="bold" /> Copy caption
              </button>
            </div>
            <p className="text-white/40 text-[0.7rem] mt-1.5">One tap copies this caption and downloads the {captionTab === "linkedin" ? "16:9 (landscape)" : "9:16 (vertical)"} clip — ready to post.</p>
          </>
        )}
      </div>
    </div>
  );
}
